<!--
  v3.5 (May 12) Transfer N units of an inventory record to another warehouse
  (also used as the "ship out" action). POSTs to
  /parts/inventory/{id}/transfer/.
-->
<template>
  <v-dialog v-model="visible" max-width="520" persistent>
    <v-card v-if="item">
      <v-card-title class="d-flex align-center pa-3">
        <v-icon class="me-2" color="info">mdi-truck-delivery</v-icon>
        Transfer / ship stock
        <v-spacer />
        <v-btn icon="mdi-close" variant="text" density="comfortable" @click="close" />
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-3">
        <div class="text-body-2 mb-1">
          <strong>{{ item.part_number || ('Part #' + item.part_id) }}</strong>
          <span v-if="item.part_name"> &mdash; {{ item.part_name }}</span>
        </div>
        <div class="text-caption text-secondary mb-3">
          From: {{ item.warehouse_name || item.warehouse_code }}
          ({{ item.warehouse_code }}) - Available:
          <strong>{{ item.quantity_available }}</strong>
        </div>

        <v-autocomplete
          v-model="form.dest_warehouse_id"
          :items="destWarehouses"
          :loading="warehousesLoading"
          item-title="label"
          item-value="id"
          label="Destination warehouse *"
          variant="outlined" density="compact" hide-details
          prepend-inner-icon="mdi-warehouse"
        />

        <v-text-field
          v-model.number="form.quantity"
          type="number" min="1" :max="item.quantity_available"
          label="Quantity"
          variant="outlined" density="compact" hide-details class="mt-3"
        />

        <v-textarea
          v-model="form.notes" rows="2"
          label="Notes (optional)"
          variant="outlined" density="compact" hide-details class="mt-3"
          placeholder="e.g. send out for repair, restock truck"
        />
      </v-card-text>
      <v-card-actions class="pa-3">
        <span v-if="errorText" class="text-error text-body-2 me-2">{{ errorText }}</span>
        <v-spacer />
        <v-btn variant="text" @click="close">Cancel</v-btn>
        <v-btn variant="elevated" color="info"
               :loading="saving" :disabled="!canSave" @click="save">
          Transfer
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch, reactive } from 'vue'
import axios from '@/api/client'
import { useNotify } from '@/composables/useNotify'

const { notify } = useNotify()

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  item: { type: Object, default: null },
})
const emit = defineEmits(['update:modelValue', 'saved'])
const visible = computed({
  get: () => props.modelValue,
  set: v => emit('update:modelValue', v),
})

const form = reactive({ dest_warehouse_id: null, quantity: 1, notes: '' })
const saving = ref(false)
const errorText = ref('')
const warehousesLoading = ref(false)
const allWarehouses = ref([])

async function loadWarehouses() {
  warehousesLoading.value = true
  try {
    const r = await axios.get('/assets/warehouses/', { params: { is_active: true } })
    allWarehouses.value = r.data?.results || r.data || []
  } catch (e) {
    console.warn('[TransferDialog] warehouses load failed', e)
  } finally {
    warehousesLoading.value = false
  }
}

const destWarehouses = computed(() =>
  allWarehouses.value
    .filter(w => w.id !== props.item?.warehouse_id)
    .map(w => ({
      id: w.id,
      label: `${w.code} - ${w.name}`,
    }))
)

watch(() => props.modelValue, v => {
  if (v) {
    form.dest_warehouse_id = null
    form.quantity = 1
    form.notes = ''
    errorText.value = ''
    loadWarehouses()
  }
})

const canSave = computed(() =>
  !!form.dest_warehouse_id
  && Number(form.quantity) > 0
  && Number(form.quantity) <= (props.item?.quantity_available || 0)
  && !saving.value
)

async function save() {
  if (!canSave.value || !props.item) return
  saving.value = true
  errorText.value = ''
  try {
    await axios.post(`/parts/inventory/${props.item.id}/transfer/`, {
      quantity: form.quantity,
      dest_warehouse_id: form.dest_warehouse_id,
      notes: form.notes,
    })
    notify.success(`Transferred ${form.quantity} unit(s) successfully.`)
    emit('saved')
    close()
  } catch (e) {
    console.error('[TransferDialog] save failed', e)
    errorText.value = e?.response?.data?.error || 'Could not transfer'
    notify.error(errorText.value)
  } finally {
    saving.value = false
  }
}

function close() { visible.value = false }
</script>
